import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { api, auth, type AuthTokens } from '../api/client'

type ProfileData = {
  id?: number
  email?: string
  role?: string
  balance?: number
  reservedCredits?: number
  concurrentLaunchLimit?: number
  name?: string
  avatarUrl?: string
  isBanned?: boolean
  isPartner?: boolean
  preferences?: {
    seriesClarificationsAutoPromptEnabled: boolean
  }
}

type PendingUser = { name?: string; email?: string; picture?: string; provider?: string }

const PROFILE_CACHE_KEY = 'auth.profile'
const USER_CACHE_KEY = 'auth.user'
const LEGACY_GOOGLE_USER_KEY = 'auth.googleUser'
const PROFILE_CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

type ProfileCacheEntry = { payload: ProfileData; fetchedAt: number }

function readProfileCache(): ProfileData | null {
  try {
    if (typeof window === 'undefined') return null
    const raw = window.localStorage.getItem(PROFILE_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ProfileCacheEntry
    if (!parsed || typeof parsed !== 'object') return null
    if (typeof parsed.fetchedAt !== 'number' || !parsed.payload) return null
    if (Date.now() - parsed.fetchedAt > PROFILE_CACHE_TTL_MS) return null
    return parsed.payload
  } catch {
    return null
  }
}

function writeProfileCache(profile: ProfileData) {
  try {
    if (typeof window === 'undefined') return
    const entry: ProfileCacheEntry = { payload: profile, fetchedAt: Date.now() }
    window.localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(entry))
  } catch {
    /* ignore */
  }
}

function clearProfileCache() {
  try {
    if (typeof window === 'undefined') return
    window.localStorage.removeItem(PROFILE_CACHE_KEY)
  } catch {
    /* ignore */
  }
}

type AuthState = {
  isAuthed: boolean
  hydrated: boolean
  user: PendingUser | null
  tokens: AuthTokens | null
  profile: ProfileData | null
  signOut: () => void
  setPendingUser: (u: PendingUser | null) => void
  completeLogin: (t: AuthTokens) => void
  refreshProfile: (force?: boolean) => Promise<void>
}

const Ctx = createContext<AuthState | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [tokens, setTokens] = useState<AuthTokens | null>(null)
  const [hydrated, setHydrated] = useState(false)
  const [user, setUser] = useState<PendingUser | null>(null)
  const [profile, setProfile] = useState<ProfileData | null>(() => readProfileCache())
  const profileRequestRef = useRef<Promise<void> | null>(null)

  useEffect(() => {
    const readTokensFromStorage = (): AuthTokens | null => {
      const raw = window.localStorage.getItem('auth.tokens')
      if (!raw) return null
      try {
        return JSON.parse(raw) as AuthTokens
      } catch {
        return null
      }
    }

    const applyTokensFromStorage = () => {
      const stored = readTokensFromStorage()
      setTokens(stored)
      return stored
    }

    const handleExpired = () => {
      setTokens(null)
      setUser(null)
      setProfile(null)
      profileRequestRef.current = null
      auth.setTokens(null)
      window.localStorage.removeItem('auth.tokens')
      window.localStorage.removeItem(USER_CACHE_KEY)
      window.localStorage.removeItem(LEGACY_GOOGLE_USER_KEY)
      clearProfileCache()
    }

    const handleBanned = () => {
      if (window.location.pathname !== '/banned') {
        window.location.replace('/banned')
      }
    }

    const initialTokens = applyTokensFromStorage()
    if (initialTokens) {
      auth.setTokens(initialTokens)
      if (auth.isExpired() || auth.isExpiringSoon(5 * 60 * 1000)) {
        void auth.refreshOnce().then(ok => {
          if (ok) {
            applyTokensFromStorage()
          } else if (auth.didLastRefreshFailIpBanned()) {
            handleBanned()
          } else {
            handleExpired()
          }
        })
      }
    }

    const savedUser = window.localStorage.getItem(USER_CACHE_KEY)
    if (savedUser) {
      try { setUser(JSON.parse(savedUser)) } catch {}
    } else {
      const legacy = window.localStorage.getItem(LEGACY_GOOGLE_USER_KEY)
      if (legacy) {
        try {
          const parsed = JSON.parse(legacy)
          setUser(parsed)
          window.localStorage.setItem(USER_CACHE_KEY, legacy)
          window.localStorage.removeItem(LEGACY_GOOGLE_USER_KEY)
        } catch {}
      }
    }
    const hasInitialTokens = Boolean(initialTokens?.accessToken || initialTokens?.refreshToken)
    const cachedProfile = readProfileCache()
    if (hasInitialTokens && cachedProfile) {
      setProfile(cachedProfile)
    } else if (!hasInitialTokens) {
      clearProfileCache()
      setProfile(null)
    }

    api.onAuthRefreshed(() => {
      applyTokensFromStorage()
      void refreshProfile(true)
    })
    api.onAuthExpired(() => {
      handleExpired()
    })
    api.onAuthBanned(() => {
      handleBanned()
    })
    if (hasInitialTokens) {
      void refreshProfile(true)
    }
    setHydrated(true)

    return () => {
      api.onAuthRefreshed(null)
      api.onAuthExpired(null)
      api.onAuthBanned(null)
    }
  }, [])

  const refreshProfile = useCallback(async (force = false) => {
    const activeTokens = auth.tokens
    if (!activeTokens.accessToken && !activeTokens.refreshToken) {
      return
    }

    const cached = !force ? readProfileCache() : null
    if (cached) {
      setProfile(cached)
      return
    }

    if (!force && profileRequestRef.current) {
      try {
        await profileRequestRef.current
      } catch {
        // ignore, will reuse cache or keep old value
      }
      return
    }

    const request = (async () => {
      try {
        const me = await api.getMe() as any
        const nextProfile: ProfileData = {
          id: typeof me.id === 'number' ? me.id : undefined,
          email: me.email,
          role: me.role,
          balance: (typeof me.balance === 'number') ? me.balance : (typeof me.balanceCredits === 'number' ? me.balanceCredits : undefined),
          reservedCredits: me.reservedCredits,
          concurrentLaunchLimit: Number.isFinite(Number(me.concurrentLaunchLimit)) ? Number(me.concurrentLaunchLimit) : undefined,
          name: me.name,
          avatarUrl: me.avatarUrl,
          isBanned: me.isBanned ?? me.banned,
          isPartner: me.isPartner,
          preferences: {
            seriesClarificationsAutoPromptEnabled:
              me.preferences?.seriesClarificationsAutoPromptEnabled !== false
          }
        }
        setProfile(nextProfile)
        writeProfileCache(nextProfile)
      } catch (err) {
        if (!force) {
          const fallback = readProfileCache()
          if (fallback) setProfile(fallback)
        }
        throw err
      }
    })()

    profileRequestRef.current = request
    try {
      await request
    } catch {
      // ignore if endpoint absent or failed
    } finally {
      profileRequestRef.current = null
    }
  }, [])

  const value = useMemo<AuthState>(() => ({
    isAuthed: !!tokens?.accessToken,
    hydrated,
    user,
    tokens,
    profile,
    signOut: () => {
      setTokens(null)
      setUser(null)
      profileRequestRef.current = null
      auth.setTokens(null)
      window.localStorage.removeItem('auth.tokens')
      window.localStorage.removeItem(USER_CACHE_KEY)
      window.localStorage.removeItem(LEGACY_GOOGLE_USER_KEY)
      clearProfileCache()
      setProfile(null)
    },
    setPendingUser: (u) => {
      setUser(u)
      if (u) {
        window.localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u))
      } else {
        window.localStorage.removeItem(USER_CACHE_KEY)
      }
      window.localStorage.removeItem(LEGACY_GOOGLE_USER_KEY)
    },
    completeLogin: (t) => {
      setTokens(t)
      auth.setTokens(t)
      window.localStorage.setItem('auth.tokens', JSON.stringify(t))
      void refreshProfile(true)
    },
    refreshProfile
  }), [tokens, user, profile, hydrated, refreshProfile])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
